import React, { useState } from 'react';
import { BarChart3, Download, Calendar, Filter, AlertTriangle, Play } from 'lucide-react';
import { useBusiness } from '@/contexts/BusinessContext';
import { useCommissionRulesByBusiness } from '@/hooks/useCommissionRules';
import { usePageTour, WelcomeModal } from '@/walkthrough';
import { adminReportsPageTour } from '@/walkthrough/tourSteps';
import {
  generateReport,
  reportToCSV,
  downloadCSV,
  getCurrentQuarterRange,
  getPreviousQuarterRange,
  getYTDRange,
  buildReferralSourceReport,
  buildSalesRepReport,
  buildAgingReport,
  type ReportData,
} from '@/services/reports';
import type { Business } from '@/types';
import { toast } from 'react-hot-toast';

export function AdminReportsPage() {
  const { currentBusiness, getBusinessLabel, businesses } = useBusiness();
  const { showWelcomeModal, startTour, skipTour, globalOptOut } = usePageTour(adminReportsPageTour);
  const [selectedBusiness, setSelectedBusiness] = useState(currentBusiness);
  const [reportType, setReportType] = useState<'detail' | 'referral' | 'rep' | 'aging'>('detail');
  const [dateRange, setDateRange] = useState<'prev-quarter' | 'current-quarter' | 'ytd' | 'custom'>('current-quarter');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [mode, setMode] = useState<'closed' | 'pipeline'>('closed');
  const [commissionStatus, setCommissionStatus] = useState<'all' | 'paid' | 'unpaid'>('all');
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);

  // Only warn about configuration when this business genuinely has no active rules.
  const { data: businessRules } = useCommissionRulesByBusiness(selectedBusiness as Business);
  const hasActiveRules = (businessRules ?? []).some((r) => r.isActive);

  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      // Get date range based on selection
      let startDate: Date;
      let endDate: Date;

      switch (dateRange) {
        case 'prev-quarter': {
          const prevQuarter = getPreviousQuarterRange();
          startDate = prevQuarter.start;
          endDate = prevQuarter.end;
          break;
        }
        case 'current-quarter': {
          const currentQuarter = getCurrentQuarterRange();
          startDate = currentQuarter.start;
          endDate = currentQuarter.end;
          break;
        }
        case 'ytd': {
          const ytd = getYTDRange();
          startDate = ytd.start;
          endDate = ytd.end;
          break;
        }
        case 'custom': {
          if (!customStartDate || !customEndDate) {
            toast.error('Select both a start and end date for a custom range');
            setLoading(false);
            return;
          }
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate + 'T23:59:59');
          break;
        }
        default: {
          const current = getCurrentQuarterRange();
          startDate = current.start;
          endDate = current.end;
        }
      }

      const data = await generateReport({
        business: selectedBusiness as Business,
        startDate,
        endDate,
        mode,
        isAdmin: true,
        commissionStatus: commissionStatus === 'all' ? undefined : commissionStatus,
      });

      setReportData(data);
      toast.success(`Report generated: ${data.projects.length} ${mode === 'pipeline' ? 'in-progress' : 'closed'} projects found`);
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (!reportData) return;
    const csv = reportToCSV(reportData);
    downloadCSV(csv, `commission-report-${Date.now()}.csv`);
    toast.success('Report exported to CSV');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">All Reports</h1>
          <p className="page-description">
            Comprehensive commission and referral reports across all reps.
          </p>
        </div>
        <div data-tour="admin-report-export" className="flex gap-2">
          <button
            className="btn btn-secondary"
            onClick={handleGenerateReport}
            disabled={loading}
          >
            <Play size={20} />
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleExportCSV}
            disabled={!reportData || reportData.projects.length === 0}
          >
            <Download size={20} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div data-tour="admin-report-filters" className="card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Business filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Business</label>
            <select
              value={selectedBusiness}
              onChange={(e) => setSelectedBusiness(e.target.value as typeof selectedBusiness)}
              className="input"
            >
              {businesses.map((b) => (
                <option key={b} value={b}>
                  {getBusinessLabel(b)}
                </option>
              ))}
            </select>
          </div>

          {/* Report type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value as typeof reportType)}
              className="input"
            >
              <option value="detail">Detailed List</option>
              <option value="referral">By Referral Source</option>
              <option value="rep">By Sales Rep</option>
              <option value="aging">Aging Unpaid</option>
            </select>
          </div>

          {/* Date range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <select
              className="input"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as typeof dateRange)}
            >
              <option value="prev-quarter">Previous Quarter</option>
              <option value="current-quarter">Current Quarter</option>
              <option value="ytd">Year to Date</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Commission status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Commission Status</label>
            <select
              className="input"
              value={commissionStatus}
              onChange={(e) => setCommissionStatus(e.target.value as typeof commissionStatus)}
            >
              <option value="all">All</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </div>

          {/* Earned vs Potential */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Report</label>
            <select
              className="input"
              value={mode}
              onChange={(e) => setMode(e.target.value as typeof mode)}
            >
              <option value="closed">Earned (closed jobs)</option>
              <option value="pipeline">Potential (in-progress jobs)</option>
            </select>
          </div>

          {/* Custom date range inputs */}
          {dateRange === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  className="input"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  className="input"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Configuration warning — only when this business has no active rules */}
      {!hasActiveRules && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="font-medium text-yellow-800">No active commission rules for {getBusinessLabel(selectedBusiness)}</p>
            <p className="text-sm text-yellow-700 mt-1">
              Commissions can't be calculated until at least one active rule exists for this business.
              Set them up on the Commission Rules page, then click Recalculate.
            </p>
          </div>
        </div>
      )}

      {/* Report content */}
      {!reportData ? (
        <div className="card">
          <div className="empty-state">
            <BarChart3 size={48} className="empty-state-icon" />
            <p className="text-lg font-medium text-gray-900">No report data</p>
            <p className="mt-1">
              Select filters and click "Generate Report" to view commission data.
            </p>
          </div>
        </div>
      ) : reportData.projects.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <BarChart3 size={48} className="empty-state-icon" />
            <p className="text-lg font-medium text-gray-900">No projects found</p>
            <p className="mt-1">
              No projects match the selected filters. Try adjusting the date range or filters.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="card">
              <p className="text-sm text-gray-600">Total Projects</p>
              <p className="text-2xl font-bold text-gray-900">{reportData.summary.totalProjects}</p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                ${reportData.summary.totalRevenue.toLocaleString()}
              </p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-600">{mode === 'pipeline' ? 'Potential' : 'Rep'} Commission</p>
              <p className="text-2xl font-bold text-gray-900">
                ${reportData.summary.totalCommission.toLocaleString()}
              </p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-600">Unpaid Rep Commission</p>
              <p className="text-2xl font-bold text-yellow-600">
                ${reportData.summary.unpaidCommission.toLocaleString()}
              </p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-600">Partner Commission</p>
              <p className="text-2xl font-bold text-gray-900">
                ${reportData.summary.totalPartnerCommission.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Grouped views */}
          {reportType === 'referral' && (
            <GroupedTable
              title="By Referral Source"
              rows={buildReferralSourceReport(reportData.projects).map((r) => ({
                name: r.companyName,
                jobCount: r.jobCount,
                revenue: r.totalRevenue,
                expected: r.expectedCommission,
                paid: r.paidCommission,
                unpaid: r.unpaidCommission,
              }))}
              nameHeader="Referral Source"
              commissionHeader="Partner Commission"
            />
          )}
          {reportType === 'rep' && (
            <GroupedTable
              title="By Sales Rep"
              rows={buildSalesRepReport(reportData.projects).map((r) => ({
                name: r.displayName,
                jobCount: r.jobCount,
                revenue: r.totalRevenue,
                expected: r.expectedCommission,
                paid: r.paidCommission,
                unpaid: r.unpaidCommission,
              }))}
              nameHeader="Sales Rep"
              commissionHeader="Rep Commission"
            />
          )}
          {reportType === 'aging' && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Age Bucket</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unpaid Count</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unpaid Amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {buildAgingReport(reportData.projects).map((b) => (
                    <tr key={b.label}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{b.label}</td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900">{b.count}</td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900">${b.totalAmount.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Report Table (detailed list) */}
          {reportType === 'detail' && (
          <div data-tour="admin-report-table" className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Business</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sales Rep</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partner</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rep Commission</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Partner Commission</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.projects.map((project) => (
                  <tr key={project.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {project.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {project.business}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {project.salesRepName || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {project.referralSourceName || 'Direct'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                      ${project.totalRevenue?.toLocaleString() || '0'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                      ${project.expectedCommission?.toLocaleString() || '0'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                      ${project.partnerExpectedCommission?.toLocaleString() || '0'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {project.commissionPaid ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          Paid
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                          Pending
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-sm font-bold text-gray-900">
                    TOTALS
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-right text-gray-900">
                    ${reportData.projects.reduce((sum, p) => sum + (p.totalRevenue || 0), 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-right text-gray-900">
                    ${reportData.projects.reduce((sum, p) => sum + (p.expectedCommission || 0), 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-right text-gray-900">
                    ${reportData.projects.reduce((sum, p) => sum + (p.partnerExpectedCommission || 0), 0).toLocaleString()}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
          )}
        </>
      )}

      <WelcomeModal
        pageName={adminReportsPageTour.pageName}
        onStartTour={startTour}
        onSkip={skipTour}
        onGlobalOptOut={globalOptOut}
        open={showWelcomeModal}
      />

      {/* Report types info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <h3 className="font-medium text-gray-900 mb-2">By Referral Source</h3>
          <p className="text-sm text-gray-600">
            See job counts, revenue, and commissions grouped by referral source company.
            Identify your top-performing partners.
          </p>
        </div>
        <div className="card">
          <h3 className="font-medium text-gray-900 mb-2">By Sales Rep</h3>
          <p className="text-sm text-gray-600">
            View performance metrics for each sales rep including closed jobs,
            total revenue, and commission totals.
          </p>
        </div>
        <div className="card">
          <h3 className="font-medium text-gray-900 mb-2">Aging Unpaid</h3>
          <p className="text-sm text-gray-600">
            Track unpaid commissions by age bucket (0-30, 31-60, 61-90, 90+ days)
            to ensure timely payments.
          </p>
        </div>
      </div>
    </div>
  );
}

// ===========================================
// Grouped table (By Referral Source / By Sales Rep)
// ===========================================

interface GroupedRow {
  name: string;
  jobCount: number;
  revenue: number;
  expected: number;
  paid: number;
  unpaid: number;
}

function GroupedTable({
  title,
  rows,
  nameHeader,
  commissionHeader,
}: {
  title: string;
  rows: GroupedRow[];
  nameHeader: string;
  commissionHeader: string;
}) {
  const totals = rows.reduce(
    (acc, r) => ({
      jobCount: acc.jobCount + r.jobCount,
      revenue: acc.revenue + r.revenue,
      expected: acc.expected + r.expected,
      paid: acc.paid + r.paid,
      unpaid: acc.unpaid + r.unpaid,
    }),
    { jobCount: 0, revenue: 0, expected: 0, paid: 0, unpaid: 0 }
  );
  return (
    <div className="bg-white rounded-lg shadow overflow-x-auto">
      <div className="px-6 py-3 border-b border-gray-100">
        <h3 className="font-semibold text-gray-900">{title}</h3>
      </div>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{nameHeader}</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Jobs</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{commissionHeader}</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Paid</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unpaid</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.map((r) => (
            <tr key={r.name}>
              <td className="px-6 py-4 text-sm font-medium text-gray-900">{r.name}</td>
              <td className="px-6 py-4 text-sm text-right text-gray-900">{r.jobCount}</td>
              <td className="px-6 py-4 text-sm text-right text-gray-900">${r.revenue.toLocaleString()}</td>
              <td className="px-6 py-4 text-sm text-right text-gray-900">${r.expected.toLocaleString()}</td>
              <td className="px-6 py-4 text-sm text-right text-green-700">${r.paid.toLocaleString()}</td>
              <td className="px-6 py-4 text-sm text-right text-yellow-700">${r.unpaid.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-gray-50">
          <tr>
            <td className="px-6 py-4 text-sm font-bold text-gray-900">TOTALS</td>
            <td className="px-6 py-4 text-sm font-bold text-right text-gray-900">{totals.jobCount}</td>
            <td className="px-6 py-4 text-sm font-bold text-right text-gray-900">${totals.revenue.toLocaleString()}</td>
            <td className="px-6 py-4 text-sm font-bold text-right text-gray-900">${totals.expected.toLocaleString()}</td>
            <td className="px-6 py-4 text-sm font-bold text-right text-green-700">${totals.paid.toLocaleString()}</td>
            <td className="px-6 py-4 text-sm font-bold text-right text-yellow-700">${totals.unpaid.toLocaleString()}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
